import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useToast } from '@/components/ui/Toast'
import { arendatorsApi } from '@/features/arendators/api/arendatorsApi'
import { ArendatorCard } from '@/features/arendators/components/ArendatorCard'
import { ArendatorModal } from '@/features/arendators/components/ArendatorModal'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { buyersApi } from '@/features/buyers/api/buyersApi'
import { BuyerCard } from '@/features/buyers/components/BuyerCard'
import { BuyerModal } from '@/features/buyers/components/BuyerModal'
import { leadDictionariesApi } from '@/lib/api/leadDictionariesApi'
import { districtsApi } from '@/lib/api/streetsApi'
import { Avatar, Box, Grid, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { leadsApi } from '../api/leadsApi'
import {
  CLIENT_TYPE_CODE,
  getSourceOptions,
  getStateOptions,
  getStatusOptions,
  LEAD_STATE,
  LEAD_STATUS,
  LEAD_TYPE_CODE,
} from '../constants/leadEnums'

/**
 * Lead Form Validation Schema
 * Returns validation schema with localized error messages
 */
const getLeadSchema = (t, isEdit = false) => z.object({
  title: z.string().max(200, t('validation.maxLength', { max: 200 })).optional(),
  first_name: z.string().min(1, t('validation.required')).max(255, t('validation.maxLength', { max: 255 })),
  last_name: isEdit
    ? z.string().max(255, t('validation.maxLength', { max: 255 })).optional()
    : z.string().min(1, t('validation.required')).max(255, t('validation.maxLength', { max: 255 })),
  middle_name: z.string().max(40, t('validation.maxLength', { max: 40 })).optional(),
  phone: z.string().min(1, t('validation.required')).max(20, t('validation.maxLength', { max: 20 })),
  email: z.string().email(t('validation.email')).optional().or(z.literal('')),
  status: z.string().min(1, t('validation.required')).max(32, t('validation.maxLength', { max: 32 })),
  state: z.string().min(1, t('validation.required')).max(32, t('validation.maxLength', { max: 32 })),
  source: z.string().min(1, t('validation.required')).max(32, t('validation.maxLength', { max: 32 })),
  source_details: z.string().max(5000, t('validation.maxLength', { max: 5000 })).optional(),
  description: z.string().max(10000, t('validation.maxLength', { max: 10000 })).optional(),
  address: z.string().optional(),
  budget_min: z.string().optional(),
  budget_max: z.string().optional(),
  next_action_text: z.string().max(255, t('validation.maxLength', { max: 255 })).optional(),
  next_step_at: z.string().optional(),
  next_action_due_at: z.string().optional(),
  // Accept string (ID), object (full entity), or null
  buyer: z.union([z.string(), z.object({}).passthrough(), z.null()]).optional(),
  arendator: z.union([z.string(), z.object({}).passthrough(), z.null()]).optional(),
  assigned_to: z.union([z.string(), z.object({}).passthrough(), z.null()]).optional(),
  // New fields for Telegram bot integration
  lead_type: z.union([z.number(), z.null()]).optional(),
  client_type: z.union([z.number(), z.null()]).optional(),
  purchase_type: z.union([z.number(), z.null()]).optional(),
  budget: z.string().optional(),
  rooms: z.string().optional(),
  districts: z.array(z.number()).optional(),
  // єОселя fields
  eoselia_first_payment: z.string().optional(),
  eoselia_income: z.string().optional(),
  eoselia_family_composition: z.string().optional(),
  eoselia_status: z.string().optional(),
  // 719 fields
  decree_719_privilege_category: z.string().optional(),
  decree_719_stage: z.string().optional(),
  decree_719_consultation: z.string().optional(),
  // Partner fields
  partner_agency: z.string().optional(),
  partner_realtor: z.union([z.number(), z.null()]).optional(),
  partner_buyer: z.union([z.number(), z.null()]).optional(),
})

/**
 * Lead Form Component
 * Reusable form for creating and editing leads
 */
export function LeadForm({ lead, onSubmit, onCancel, isLoading }) {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useAuth()

  const {
    register,
    handleSubmit,
    setError,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(getLeadSchema(t, !!lead)),
    defaultValues: {
      title: lead?.title || '',
      first_name: lead?.first_name || '',
      last_name: lead?.last_name || '',
      middle_name: lead?.middle_name || '',
      phone: lead?.phone || '',
      email: lead?.email || '',
      status: lead?.status || LEAD_STATUS.NEW,
      state: lead?.state || LEAD_STATE.NEW,
      source: lead?.source || '',
      source_details: lead?.source_details || '',
      description: lead?.description || '',
      address: lead?.address || '',
      budget_min: lead?.budget_min || '',
      budget_max: lead?.budget_max || '',
      next_action_text: lead?.next_action_text || '',
      next_step_at: lead?.next_step_at?.split('T')[0] || '',
      next_action_due_at: lead?.next_action_due_at?.split('T')[0] || '',
      buyer: lead?.buyer || null,
      arendator: lead?.arendator || null,
      assigned_to: lead?.assigned_to || null,
      // New fields
      lead_type: lead?.lead_type || null,
      client_type: lead?.client_type || null,
      purchase_type: lead?.purchase_type || null,
      budget: lead?.budget || '',
      rooms: lead?.rooms || '',
      districts: lead?.districts || [],
      // єОселя fields
      eoselia_first_payment: lead?.eoselia_first_payment || '',
      eoselia_income: lead?.eoselia_income || '',
      eoselia_family_composition: lead?.eoselia_family_composition || '',
      eoselia_status: lead?.eoselia_status || '',
      // 719 fields
      decree_719_privilege_category: lead?.decree_719_privilege_category || '',
      decree_719_stage: lead?.decree_719_stage || '',
      decree_719_consultation: lead?.decree_719_consultation || '',
      // Partner fields
      partner_agency: lead?.partner_agency || '',
      partner_realtor: lead?.partner_realtor || null,
      partner_buyer: lead?.partner_buyer || null,
    },
  })

  // Watch assigned_to field for "Assign to me" link
  const assignedToValue = watch('assigned_to')
  const buyerValue = watch('buyer')
  const arendatorValue = watch('arendator')

  // Watch for conditional sections
  const leadTypeValue = watch('lead_type')
  const clientTypeValue = watch('client_type')

  // Collapsible sections state - auto-expand if value exists
  const [isBuyerExpanded, setIsBuyerExpanded] = useState(!!lead?.buyer)
  const [isArendatorExpanded, setIsArendatorExpanded] = useState(!!lead?.arendator)
  const [isEditingAssignee, setIsEditingAssignee] = useState(false)

  // Dictionary options state
  const [leadTypes, setLeadTypes] = useState([])
  const [clientTypes, setClientTypes] = useState([])
  const [purchaseTypes, setPurchaseTypes] = useState([])
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState(true)

  // Modal states
  const [isBuyerModalOpen, setIsBuyerModalOpen] = useState(false)
  const [isArendatorModalOpen, setIsArendatorModalOpen] = useState(false)

  // Full buyer/arendator data (loaded from API if only ID is present)
  const [buyerData, setBuyerData] = useState(null)
  const [arendatorData, setArendatorData] = useState(null)
  const [isLoadingBuyer, setIsLoadingBuyer] = useState(false)
  const [isLoadingArendator, setIsLoadingArendator] = useState(false)

  // Load dictionaries on mount
  useEffect(() => {
    const loadDictionaries = async () => {
      setIsLoadingDictionaries(true)
      try {
        const [leadTypesRes, clientTypesRes, purchaseTypesRes] = await Promise.all([
          leadDictionariesApi.leadTypesApi.getAll({ page_size: 100 }),
          leadDictionariesApi.clientTypesApi.getAll({ page_size: 100 }),
          leadDictionariesApi.purchaseTypesApi.getAll({ page_size: 100 }),
        ])
        setLeadTypes(leadTypesRes.data || [])
        setClientTypes(clientTypesRes.data || [])
        setPurchaseTypes(purchaseTypesRes.data || [])
      } catch (error) {
        console.error('Failed to load lead dictionaries:', error)
        toast.error({
          title: t('errors.loadError'),
          description: t('leads.dictionariesLoadError'),
        })
      } finally {
        setIsLoadingDictionaries(false)
      }
    }
    loadDictionaries()
  }, [t, toast])

  // Auto-expand sections when value is set
  useEffect(() => {
    if (buyerValue) setIsBuyerExpanded(true)
  }, [buyerValue])

  useEffect(() => {
    if (arendatorValue) setIsArendatorExpanded(true)
  }, [arendatorValue])

  // Load full buyer data if only ID is present
  useEffect(() => {
    const loadBuyerData = async () => {
      if (buyerValue) {
        // Check if buyerValue is just an ID or full object
        if (typeof buyerValue === 'number' || typeof buyerValue === 'string') {
          setIsLoadingBuyer(true)
          try {
            const response = await buyersApi.getById(buyerValue)
            setBuyerData(response.data)
          } catch (error) {
            console.error('Failed to load buyer data:', error)
            setBuyerData(null)
          } finally {
            setIsLoadingBuyer(false)
          }
        } else if (typeof buyerValue === 'object' && buyerValue.id) {
          // If it's already a full object, use it
          setBuyerData(buyerValue)
        }
      } else {
        setBuyerData(null)
      }
    }
    loadBuyerData()
  }, [buyerValue])

  // Load full arendator data if only ID is present
  useEffect(() => {
    const loadArendatorData = async () => {
      if (arendatorValue) {
        // Check if arendatorValue is just an ID or full object
        if (typeof arendatorValue === 'number' || typeof arendatorValue === 'string') {
          setIsLoadingArendator(true)
          try {
            const response = await arendatorsApi.getById(arendatorValue)
            setArendatorData(response.data)
          } catch (error) {
            console.error('Failed to load arendator data:', error)
            setArendatorData(null)
          } finally {
            setIsLoadingArendator(false)
          }
        } else if (typeof arendatorValue === 'object' && arendatorValue.id) {
          // If it's already a full object, use it
          setArendatorData(arendatorValue)
        }
      } else {
        setArendatorData(null)
      }
    }
    loadArendatorData()
  }, [arendatorValue])

  // Close assignee editing when no value
  useEffect(() => {
    if (!assignedToValue) setIsEditingAssignee(false)
  }, [assignedToValue])

  // Handle "Assign to me"
  function handleAssignToMe(e) {
    e.preventDefault()
    if (user) {
      // Set the full user object, not just the ID
      setValue('assigned_to', user, { shouldValidate: false })
    }
  }

  /**
   * Transform form data before submit
   * Convert empty strings to null for numeric/foreign key/date fields
   * Remove null/empty text fields to avoid API validation errors
   */
  const transformFormData = (data) => {
    const transformed = {
      ...data,
      // Convert empty strings to null for numeric fields
      budget_min: data.budget_min ? Number(data.budget_min) : null,
      budget_max: data.budget_max ? Number(data.budget_max) : null,
      // Convert to IDs if objects were passed from autocomplete, or parse numbers
      buyer: typeof data.buyer === 'object' && data.buyer !== null
        ? Number(data.buyer.id || data.buyer.buyer_id || data.buyer.pk)
        : data.buyer
          ? Number(data.buyer)
          : null,
      arendator: typeof data.arendator === 'object' && data.arendator !== null
        ? Number(data.arendator.id || data.arendator.arendator_id || data.arendator.pk)
        : data.arendator
          ? Number(data.arendator)
          : null,
      assigned_to: typeof data.assigned_to === 'object' && data.assigned_to !== null
        ? Number(data.assigned_to.id || data.assigned_to.user_id || data.assigned_to.pk)
        : data.assigned_to
          ? Number(data.assigned_to)
          : null,
      // Convert empty strings to null for dates
      next_step_at: data.next_step_at || null,
      next_action_due_at: data.next_action_due_at || null,
      // Keep text fields as empty strings or remove them if empty
      source_details: data.source_details || undefined,
      next_action_text: data.next_action_text || undefined,
      // New fields - foreign keys (already numbers or null)
      lead_type: data.lead_type || null,
      client_type: data.client_type || null,
      purchase_type: data.purchase_type || null,
      // New fields - text (budget and rooms kept as strings)
      budget: data.budget || undefined,
      rooms: data.rooms || undefined,
      // Districts - array of IDs
      districts: Array.isArray(data.districts) && data.districts.length > 0 ? data.districts : undefined,
      // єОселя fields
      eoselia_first_payment: data.eoselia_first_payment ? Number(data.eoselia_first_payment) : null,
      eoselia_income: data.eoselia_income ? Number(data.eoselia_income) : null,
      eoselia_family_composition: data.eoselia_family_composition || undefined,
      eoselia_status: data.eoselia_status || undefined,
      // 719 fields
      decree_719_privilege_category: data.decree_719_privilege_category || undefined,
      decree_719_stage: data.decree_719_stage || undefined,
      decree_719_consultation: data.decree_719_consultation || undefined,
      // Partner fields
      partner_agency: data.partner_agency || undefined,
      partner_realtor: data.partner_realtor || null,
      partner_buyer: data.partner_buyer || null,
    }

    // Remove undefined fields to avoid sending them to API
    Object.keys(transformed).forEach(key => {
      if (transformed[key] === undefined) {
        delete transformed[key]
      }
    })

    return transformed
  }

  const handleFormSubmit = async (data) => {
    try {
      const transformedData = transformFormData(data)
      await onSubmit(transformedData)
    } catch (error) {
      // Handle field validation errors from API
      if (error?.error?.details && typeof error.error.details === 'object') {
        const fieldErrors = error.error.details

        // Set error for each field
        Object.keys(fieldErrors).forEach((fieldName) => {
          const errorMessages = fieldErrors[fieldName]
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            setError(fieldName, {
              type: 'server',
              message: errorMessages[0], // Take first error message
            })
          }
        })

        // Show general error toast
        toast.error({
          title: t('errors.validationError'),
          description: error.message || t('errors.validationErrorDesc'),
        })
      } else {
        // Re-throw other errors to be handled by parent
        throw error
      }
    }
  }

  return (
    <form onSubmit={ handleSubmit(handleFormSubmit) }>
      {/* Two-column layout: Main content + Details sidebar */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 340px' }} gap={ 6 }>
        {/* Main Content Area */}
        <VStack spacing={ 6 } align="stretch">
          {/* Title (separate from contact info) */}
          <Box>
            <Input
              label={t('leads.leadTitle')}
              placeholder={t('leads.titlePlaceholder')}
              error={ errors.title?.message }
              maxLength={200}
              { ...register('title') }
            />
          </Box>

          {/* Basic Information */}
          <Fieldset>
            <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
              {t('leads.contactInformation')}
            </Fieldset.Legend>
            <VStack spacing={ 4 } align="stretch" mt={ 2 }>
              <HStack spacing={ 4 } align="flex-start">
                <Box maxW="250px">
                  <Input
                    label={`${t('leads.firstName')} *`}
                    placeholder={t('leads.firstNamePlaceholder')}
                    error={ errors.first_name?.message }
                    maxLength={255}
                    { ...register('first_name') }
                  />
                </Box>
                <Box maxW="250px">
                  <Input
                    label={`${t('leads.lastName')} *`}
                    placeholder={t('leads.lastNamePlaceholder')}
                    error={ errors.last_name?.message }
                    maxLength={255}
                    { ...register('last_name') }
                  />
                </Box>
                <Box maxW="250px">
                  <Input
                    label={t('leads.middleName')}
                    placeholder={t('leads.middleNamePlaceholder')}
                    error={ errors.middle_name?.message }
                    maxLength={40}
                    { ...register('middle_name') }
                  />
                </Box>
              </HStack>
              <HStack spacing={ 4 } align="flex-start">
                <Box maxW="250px">
                  <Input
                    label={`${t('leads.phone')} *`}
                    placeholder={t('leads.phonePlaceholder')}
                    error={ errors.phone?.message }
                    maxLength={20}
                    { ...register('phone') }
                  />
                </Box>
                <Box maxW="300px">
                  <Input
                    type="email"
                    label={t('leads.email')}
                    placeholder={t('leads.emailPlaceholder')}
                    error={ errors.email?.message }
                    { ...register('email') }
                  />
                </Box>
              </HStack>
              <Input
                label={t('leads.address')}
                placeholder={t('leads.addressPlaceholder')}
                error={ errors.address?.message }
                { ...register('address') }
              />
              <Textarea
                label={t('leads.description')}
                placeholder={t('leads.descriptionPlaceholder')}
                rows={ 4 }
                error={ errors.description?.message }
                maxLength={10000}
                { ...register('description') }
              />
            </VStack>
          </Fieldset>

          {/* Property Selection Parameters - New Section */}
          <Fieldset>
            <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
              {t('leads.propertySelectionParameters')}
            </Fieldset.Legend>
            <VStack spacing={ 4 } align="stretch" mt={ 2 }>
              {/* Lead Type, Client Type, Purchase Type */}
              <Grid templateColumns="repeat(3, 1fr)" gap={ 4 }>
                <Controller
                  name="lead_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('leads.leadType')}
                      placeholder={t('leads.selectLeadType')}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      error={errors.lead_type?.message}
                      disabled={isLoadingDictionaries}
                    >
                      <option value="">{t('leads.selectLeadType')}</option>
                      {leadTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <Controller
                  name="client_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('leads.clientType')}
                      placeholder={t('leads.selectClientType')}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      error={errors.client_type?.message}
                      disabled={isLoadingDictionaries}
                    >
                      <option value="">{t('leads.selectClientType')}</option>
                      {clientTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <Controller
                  name="purchase_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('leads.purchaseType')}
                      placeholder={t('leads.selectPurchaseType')}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      error={errors.purchase_type?.message}
                      disabled={isLoadingDictionaries}
                    >
                      <option value="">{t('leads.selectPurchaseType')}</option>
                      {purchaseTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </Grid>

              {/* Budget and Rooms */}
              <Grid templateColumns="1fr 1fr" gap={ 4 }>
                <Box>
                  <Input
                    label={t('leads.budget')}
                    placeholder={t('leads.budgetPlaceholder')}
                    error={errors.budget?.message}
                    helperText={t('leads.budgetHelperText')}
                    { ...register('budget') }
                  />
                  {/* Show readonly calculated budget range */}
                  {(lead?.budget_from || lead?.budget_to) && (
                    <Text fontSize="xs" color="#5E6C84" mt={ 1 }>
                      {t('leads.budgetRange')}: {lead.budget_from ? `${lead.budget_from.toLocaleString()} грн` : '—'} - {lead.budget_to ? `${lead.budget_to.toLocaleString()} грн` : '—'}
                    </Text>
                  )}
                </Box>
                <Box>
                  <Input
                    label={t('leads.rooms')}
                    placeholder={t('leads.roomsPlaceholder')}
                    error={errors.rooms?.message}
                    helperText={t('leads.roomsHelperText')}
                    { ...register('rooms') }
                  />
                  {/* Show readonly calculated rooms range */}
                  {(lead?.rooms_from || lead?.rooms_to) && (
                    <Text fontSize="xs" color="#5E6C84" mt={ 1 }>
                      {t('leads.roomsRange')}: {lead.rooms_from || '—'} - {lead.rooms_to || '—'}
                    </Text>
                  )}
                </Box>
              </Grid>

              {/* Districts */}
              <Controller
                name="districts"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label={t('leads.districts')}
                    placeholder={t('leads.selectDistricts')}
                    value={field.value || []}
                    onChange={field.onChange}
                    fetchOptions={(params) => districtsApi.getAll(params)}
                    getOptionLabel={(item) => item.district}
                    getOptionValue={(item) => item.id}
                    error={errors.districts?.message}
                    emptyText={t('leads.noDistrictsFound')}
                  />
                )}
              />
            </VStack>
          </Fieldset>

          {/* Conditional Section: єОселя */}
          {clientTypeValue && (() => {
            const selectedClientType = clientTypes.find(t => t.id === clientTypeValue)
            return selectedClientType?.code === CLIENT_TYPE_CODE.EOSELIA
          })() && (
            <Fieldset>
              <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
                {t('leads.eoseliaSection')}
              </Fieldset.Legend>
              <VStack spacing={ 4 } align="stretch" mt={ 2 }>
                <Grid templateColumns="1fr 1fr" gap={ 4 }>
                  <Input
                    type="number"
                    label={t('leads.eoseliaFirstPayment')}
                    placeholder={t('leads.eoseliaFirstPaymentPlaceholder')}
                    error={errors.eoselia_first_payment?.message}
                    { ...register('eoselia_first_payment') }
                  />
                  <Input
                    type="number"
                    label={t('leads.eoseliaIncome')}
                    placeholder={t('leads.eoseliaIncomePlaceholder')}
                    error={errors.eoselia_income?.message}
                    { ...register('eoselia_income') }
                  />
                </Grid>
                <Textarea
                  label={t('leads.eoseliaFamilyComposition')}
                  placeholder={t('leads.eoseliaFamilyCompositionPlaceholder')}
                  rows={ 3 }
                  error={errors.eoselia_family_composition?.message}
                  { ...register('eoselia_family_composition') }
                />
                <Input
                  label={t('leads.eoseliaStatus')}
                  placeholder={t('leads.eoseliaStatusPlaceholder')}
                  error={errors.eoselia_status?.message}
                  { ...register('eoselia_status') }
                />
              </VStack>
            </Fieldset>
          )}

          {/* Conditional Section: Постанова 719 */}
          {clientTypeValue && (() => {
            const selectedClientType = clientTypes.find(t => t.id === clientTypeValue)
            return selectedClientType?.code === CLIENT_TYPE_CODE.DECREE_719
          })() && (
            <Fieldset>
              <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
                {t('leads.decree719Section')}
              </Fieldset.Legend>
              <VStack spacing={ 4 } align="stretch" mt={ 2 }>
                <Grid templateColumns="1fr 1fr" gap={ 4 }>
                  <Input
                    label={t('leads.decree719PrivilegeCategory')}
                    placeholder={t('leads.decree719PrivilegeCategoryPlaceholder')}
                    error={errors.decree_719_privilege_category?.message}
                    { ...register('decree_719_privilege_category') }
                  />
                  <Input
                    label={t('leads.decree719Stage')}
                    placeholder={t('leads.decree719StagePlaceholder')}
                    error={errors.decree_719_stage?.message}
                    { ...register('decree_719_stage') }
                  />
                </Grid>
                <Textarea
                  label={t('leads.decree719Consultation')}
                  placeholder={t('leads.decree719ConsultationPlaceholder')}
                  rows={ 3 }
                  error={errors.decree_719_consultation?.message}
                  { ...register('decree_719_consultation') }
                />
              </VStack>
            </Fieldset>
          )}

          {/* Conditional Section: Партнер */}
          {leadTypeValue && (() => {
            const selectedLeadType = leadTypes.find(t => t.id === leadTypeValue)
            return selectedLeadType?.code === LEAD_TYPE_CODE.PARTNER_REQUEST
          })() && (
            <Fieldset>
              <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
                {t('leads.partnerSection')}
              </Fieldset.Legend>
              <VStack spacing={ 4 } align="stretch" mt={ 2 }>
                <Input
                  label={t('leads.partnerAgency')}
                  placeholder={t('leads.partnerAgencyPlaceholder')}
                  error={errors.partner_agency?.message}
                  { ...register('partner_agency') }
                />
                <Grid templateColumns="1fr 1fr" gap={ 4 }>
                  <Controller
                    name="partner_realtor"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        label={t('leads.partnerRealtor')}
                        placeholder={t('leads.partnerRealtorPlaceholder')}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        error={errors.partner_realtor?.message}
                      />
                    )}
                  />
                  <Controller
                    name="partner_buyer"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        label={t('leads.partnerBuyer')}
                        placeholder={t('leads.partnerBuyerPlaceholder')}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        error={errors.partner_buyer?.message}
                      />
                    )}
                  />
                </Grid>
              </VStack>
            </Fieldset>
          )}

          {/* Source Information */}
          <Fieldset>
            <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
              {t('leads.sourceInformation')}
            </Fieldset.Legend>
            <Grid templateColumns="minmax(180px, 1fr) 4fr" gap={ 4 }>
              <Select
                label={`${t('leads.source')} *`}
                placeholder={t('leads.selectSource')}
                options={ getSourceOptions() }
                error={ errors.source?.message }
                { ...register('source') }
              />
              <Input
                label={t('leads.sourceDetails')}
                placeholder={t('leads.sourceDetailsPlaceholder')}
                error={ errors.source_details?.message }
                maxLength={5000}
                { ...register('source_details') }
              />
            </Grid>
          </Fieldset>

          {/* Next Action */}
          <Fieldset>
            <Fieldset.Legend color="#172B4D" fontWeight="semibold" fontSize="lg">
              {t('leads.nextActions')}
            </Fieldset.Legend>
            <Grid templateColumns="1fr 200px" gap={ 4 } alignItems="start">
              <Textarea
                label={t('leads.nextActionText')}
                placeholder={t('leads.nextActionTextPlaceholder')}
                error={ errors.next_action_text?.message }
                maxLength={255}
                { ...register('next_action_text') }
                rows={ 4 }
              />
              <VStack spacing={ 4 } align="stretch">
                <Input
                  type="date"
                  label={t('leads.nextStepDate')}
                  error={ errors.next_step_at?.message }
                  { ...register('next_step_at') }
                />
                <Input
                  type="date"
                  label={t('leads.nextActionDueDate')}
                  error={ errors.next_action_due_at?.message }
                  { ...register('next_action_due_at') }
                />
              </VStack>
            </Grid>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={ 3 } pt={ 4 }>
            { onCancel && (
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={ onCancel }
              >
                {t('common.cancel')}
              </Button>
            ) }
            <Button
              type="submit"
              size="md"
              isLoading={ isLoading }
            >
              { lead ? t('leads.updateLead') : t('leads.createLead') }
            </Button>
          </HStack>
        </VStack>

        {/* Details Sidebar */}
        <Box
          border="1px solid"
          borderColor="#DFE1E6"
          borderRadius="sm"
          bg="#FAFBFC"
          p={ 4 }
          alignSelf="start"
          position="sticky"
          top="20px"
        >
          <Heading size="sm" mb={ 4 } color="#172B4D">
            {t('leads.details')}
          </Heading>
          <VStack spacing={ 4 } align="stretch">
            {/* Status */}
            <Select
              label={`${t('leads.status')} *`}
              placeholder={t('leads.selectStatus')}
              options={ getStatusOptions() }
              error={ errors.status?.message }
              { ...register('status') }
            />

            {/* Assigned To */}
            <Box>
              <Controller
                name="assigned_to"
                control={control}
                render={({ field }) => (
                  <Box>
                    {field.value && !isEditingAssignee ? (
                      // Show assigned user with avatar (Jira-style)
                      <Box>
                        <Text fontSize="sm" fontWeight="500" color="#172B4D" mb={ 2 }>
                          {t('leads.assignedTo')}
                        </Text>
                        <HStack
                          p={ 2 }
                          spacing={ 2 }
                          borderRadius="sm"
                          border="1px solid"
                          borderColor="#DFE1E6"
                          bg="white"
                          cursor="pointer"
                          onClick={() => setIsEditingAssignee(true)}
                          _hover={{ bg: '#FAFBFC', borderColor: '#B3BAC5' }}
                        >
                          <Avatar.Root size="xs">
                            <Avatar.Fallback>
                              {(() => {
                                const user = field.value
                                if (user.first_name || user.last_name) {
                                  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                }
                                return (user.username || user.email || 'U')[0].toUpperCase()
                              })()}
                            </Avatar.Fallback>
                          </Avatar.Root>
                          <Text fontSize="sm" color="#172B4D" flex={ 1 }>
                            {(() => {
                              const user = field.value
                              if (user.first_name || user.last_name) {
                                return `${user.first_name || ''} ${user.last_name || ''}`.trim()
                              }
                              return user.username || user.email || 'Unknown'
                            })()}
                          </Text>
                          <Button
                            size="xs"
                            variant="ghost"
                            color="#DE350B"
                            onClick={(e) => {
                              e.stopPropagation()
                              field.onChange(null)
                            }}
                            _hover={{ bg: '#FFEBE6' }}
                          >
                            ×
                          </Button>
                        </HStack>
                      </Box>
                    ) : (
                      // Show search field when no one assigned or editing
                      <AutocompleteSelect
                        label={t('leads.assignedTo')}
                        placeholder={t('leads.searchUsers')}
                        value={field.value}
                        onChange={(item) => {
                          field.onChange(item || null)
                          setIsEditingAssignee(false)
                        }}
                        fetchOptions={leadsApi.getUsersAutocomplete}
                        getOptionLabel={(item) => {
                          if (item.first_name || item.last_name) {
                            return `${item.first_name || ''} ${item.last_name || ''}`.trim()
                          }
                          return item.username || item.email || 'Unknown'
                        }}
                        getOptionValue={(item) => item.id}
                        error={errors.assigned_to?.message}
                        showAvatar={true}
                        emptyText={t('leads.noUsersFound')}
                        autoOpen={isEditingAssignee}
                      />
                    )}
                  </Box>
                )}
              />
              {/* Assign to me link */}
              {user && (() => {
                const userId = user.id || user.user_id || user.pk
                const assignedId = typeof assignedToValue === 'object'
                  ? (assignedToValue?.id || assignedToValue?.user_id || assignedToValue?.pk)
                  : assignedToValue
                return !assignedToValue || assignedId !== userId
              })() && (
                <a
                  href="#"
                  onClick={handleAssignToMe}
                  style={{
                    display: 'inline-block',
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#0052CC',
                    textDecoration: 'none',
                    fontWeight: '500',
                  }}
                  onMouseEnter={(e) => { e.target.style.textDecoration = 'underline' }}
                  onMouseLeave={(e) => { e.target.style.textDecoration = 'none' }}
                >
                  {t('leads.assignToMe')}
                </a>
              )}
            </Box>

            {/* State */}
            <Select
              label={`${t('leads.state')} *`}
              placeholder={t('leads.selectState')}
              options={ getStateOptions() }
              error={ errors.state?.message }
              { ...register('state') }
            />

            {/* Budget Range - Read Only */}
            {lead && (lead.budget_from || lead.budget_to) && (
              <Box>
                <Text fontSize="xs" fontWeight="600" color="#5E6C84" mb={ 1 } textTransform="uppercase">
                  {t('leads.budgetRange')}
                </Text>
                <Text fontSize="sm" color="#172B4D">
                  {lead.budget_from ? `${lead.budget_from.toLocaleString()} грн` : '—'} - {lead.budget_to ? `${lead.budget_to.toLocaleString()} грн` : '—'}
                </Text>
              </Box>
            )}

            {/* Timestamps - Read Only */}
            {lead && (lead.created_at || lead.updated_at) && (
              <Box mt={ 4 } pt={ 4 } borderTop="1px solid" borderColor="#EBECF0">
                <VStack spacing={ 3 } align="stretch">
                  {lead.created_at && (
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="#5E6C84" mb={ 1 } textTransform="uppercase">
                        {t('leads.createdAt')}
                      </Text>
                      <Text fontSize="sm" color="#172B4D">
                        {format(new Date(lead.created_at), 'dd.MM.yyyy HH:mm')}
                      </Text>
                    </Box>
                  )}
                  {lead.updated_at && (
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="#5E6C84" mb={ 1 } textTransform="uppercase">
                        {t('leads.updatedAt')}
                      </Text>
                      <Text fontSize="sm" color="#172B4D">
                        {format(new Date(lead.updated_at), 'dd.MM.yyyy HH:mm')}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      </Grid>

      {/* Additional Information - Collapsible Sections */}
      <VStack spacing={ 4 } align="stretch" mt={ 6 }>
        {/* Buyer Section */}
        <Box
          border="1px solid"
          borderColor="#DFE1E6"
          borderRadius="sm"
          bg="white"
        >
          <HStack
            justify="space-between"
            p={ 4 }
            cursor="pointer"
            onClick={() => setIsBuyerExpanded(!isBuyerExpanded)}
            _hover={{ bg: '#FAFBFC' }}
          >
            <Heading size="sm" color="#172B4D">
              {t('leads.buyer')}
            </Heading>
            <Text fontSize="lg" color="#5E6C84">
              {isBuyerExpanded ? '−' : '+'}
            </Text>
          </HStack>
          {isBuyerExpanded && (
            <Box p={ 4 } pt={ 0 }>
              {isLoadingBuyer ? (
                <Text fontSize="sm" color="#5E6C84">{t('common.loading')}</Text>
              ) : buyerData ? (
                <BuyerCard
                  buyer={buyerData}
                  onEdit={() => setIsBuyerModalOpen(true)}
                  onRemove={() => {
                    setValue('buyer', null)
                    setBuyerData(null)
                  }}
                />
              ) : (
                <Controller
                  name="buyer"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('leads.buyer')}
                      placeholder={t('leads.searchBuyers')}
                      value={field.value}
                      onChange={(item) => field.onChange(item || null)}
                      fetchOptions={leadsApi.getBuyersAutocomplete}
                      getOptionLabel={(item) => {
                        const name = item.client_name || item.name
                        const phone = item.client_phone || item.phone_first
                        if (phone && name) return `${phone} - ${name}`
                        if (phone) return phone
                        if (name) return name
                        if (item.email) return item.email
                        return `Buyer #${item.id}`
                      }}
                      getOptionValue={(item) => item.id}
                      error={errors.buyer?.message}
                      emptyText={t('leads.noBuyersFound')}
                    />
                  )}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Arendator Section */}
        <Box
          border="1px solid"
          borderColor="#DFE1E6"
          borderRadius="sm"
          bg="white"
        >
          <HStack
            justify="space-between"
            p={ 4 }
            cursor="pointer"
            onClick={() => setIsArendatorExpanded(!isArendatorExpanded)}
            _hover={{ bg: '#FAFBFC' }}
          >
            <Heading size="sm" color="#172B4D">
              {t('leads.arendator')}
            </Heading>
            <Text fontSize="lg" color="#5E6C84">
              {isArendatorExpanded ? '−' : '+'}
            </Text>
          </HStack>
          {isArendatorExpanded && (
            <Box p={ 4 } pt={ 0 }>
              {isLoadingArendator ? (
                <Text fontSize="sm" color="#5E6C84">{t('common.loading')}</Text>
              ) : arendatorData ? (
                <ArendatorCard
                  arendator={arendatorData}
                  onEdit={() => setIsArendatorModalOpen(true)}
                  onRemove={() => {
                    setValue('arendator', null)
                    setArendatorData(null)
                  }}
                />
              ) : (
                <Controller
                  name="arendator"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('leads.arendator')}
                      placeholder={t('leads.searchArendators')}
                      value={field.value}
                      onChange={(item) => field.onChange(item || null)}
                      fetchOptions={leadsApi.getArendatorsAutocomplete}
                      getOptionLabel={(item) => {
                        const name = item.client_name || item.name
                        const phone = item.client_phone || item.phone_first
                        if (phone && name) return `${phone} - ${name}`
                        if (phone) return phone
                        if (name) return name
                        if (item.email) return item.email
                        return `Arendator #${item.id}`
                      }}
                      getOptionValue={(item) => item.id}
                      error={errors.arendator?.message}
                      emptyText={t('leads.noArendatorsFound')}
                    />
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      </VStack>

      {/* Modals */}
      <BuyerModal
        isOpen={isBuyerModalOpen}
        onClose={() => setIsBuyerModalOpen(false)}
        buyer={buyerData}
        onSuccess={(updatedBuyer) => {
          setBuyerData(updatedBuyer)
          setValue('buyer', updatedBuyer)
        }}
      />
      <ArendatorModal
        isOpen={isArendatorModalOpen}
        onClose={() => setIsArendatorModalOpen(false)}
        arendator={arendatorData}
        onSuccess={(updatedArendator) => {
          setArendatorData(updatedArendator)
          setValue('arendator', updatedArendator)
        }}
      />
    </form>
  )
}

export default LeadForm
